// frontend/src/components/Charts/dataPipeline.ts

import { startOfWeek, startOfMonth, startOfQuarter, startOfYear, format } from 'date-fns'
import { parseDateLocal } from '~/util/date'
import { stepLightness } from '~/lib/vtLadder'
import { fiscalYearStart } from '~/util/fiscalYear'
// type-only: erased at build; keep it `import type` — a VALUE import would couple the chart
// pipeline to the state layer (fiscalYear.ts's purity note relies on this).
import type { GranularityUnit } from '~/state/useGranularityState'

// Re-exported for the many chart-layer importers that already take it from here. ONE definition —
// the zod enum in useGranularityState is the source of truth.
export type { GranularityUnit }

export interface SeriesPoint {
  time: string
  [seriesKey: string]: string | number | undefined
}

export interface Group {
  key: string
  label: string
  color: { hex: string; opacity: number }
}

/** Row 15-light: the previous-period baseline panel renders all series in neutral gray (shared by
 *  BaselinePanel and the DynamicsPanelsTile baseline panels). Lives here — not in
 *  ChartCanvas — so BaselinePanel can import it without a ChartCanvas↔BaselinePanel cycle. */
export const BASELINE_COLOR = { hex: '#9e9e9e', opacity: 0.6 }

/** The value_type series suffix the chart pivots emit (`${base} · ${value_type}`).
 *  MUST match the literal in TimelineChart/AbsoluteBarChart pivot (space, U+00B7, space). */
const SERIES_VT_SEP = ' · '

export function assignGroups(
  seriesKeys: string[],
  palette: string[],
  vtLabels: Record<string, string> = {},
  labelForBase: (base: string) => string = (base) => base,
  colorForBase?: (base: string) => string | undefined,
): Group[] {
  // Pre-scan (before the .map): sorted raw-VT list per base drives the ladder index.
  const vtByBase = new Map<string, string[]>()
  for (const k of seriesKeys) {
    const sep = k.lastIndexOf(SERIES_VT_SEP)
    if (sep === -1) continue
    const b = k.slice(0, sep)
    const list = vtByBase.get(b) ?? []
    if (!list.includes(k.slice(sep + SERIES_VT_SEP.length)))
      list.push(k.slice(sep + SERIES_VT_SEP.length))
    vtByBase.set(b, list)
  }
  for (const list of vtByBase.values()) list.sort()

  // Wave-5 §2 (D3): the chart's sole non-empty NON-KEYED VT-suffixed base (exactly one
  // existing) joins the empty base in sorted-VT slot indexing below. With ≥2 such bases the
  // positional fallback stands — two catalog-miss dim values sharing a VT set must not
  // collapse to one color.
  const nonKeyedVtBases = [...vtByBase.keys()].filter(
    (b) => b !== '' && (colorForBase ? colorForBase(b) : undefined) === undefined,
  )
  const soleNonKeyedVtBase = nonKeyedVtBases.length === 1 ? nonKeyedVtBases[0]! : null

  return seriesKeys.map((key, i) => {
    // Split base from the optional ` · <value_type>` suffix; translate base and suffix
    // independently so a display label containing ' · ' can never confuse the split.
    const sep = key.lastIndexOf(SERIES_VT_SEP)
    const rawBase = sep === -1 ? key : key.slice(0, sep)
    const rawVt = sep === -1 ? null : key.slice(sep + SERIES_VT_SEP.length)
    const niceVt = rawVt === null ? null : (vtLabels[rawVt] ?? rawVt)
    // Value-keyed when the caller can map a dim value to its catalog slot (spec D3 — cross-page
    // identity); positional otherwise. D5 amendment: a value-keyed base carrying ≥2 VTs gets a
    // shared-hue lightness ladder (stepLightness), indexed by the SORTED raw-VT list so the
    // order is deterministic across pages/pins/sessions.
    const keyed = colorForBase ? colorForBase(rawBase) : undefined
    const vts = rawVt !== null ? vtByBase.get(rawBase)! : []
    const hex =
      keyed !== undefined && rawVt !== null && vts.length >= 2
        ? stepLightness(keyed, vts.indexOf(rawVt), vts.length)
        : keyed !== undefined
          ? keyed
          : rawVt !== null && (rawBase === '' || rawBase === soleNonKeyedVtBase)
            ? // Sorted-VT slot indexing — wave-4 rider #2 (empty base, unconditional) widened
              // by wave-5 §2 (the sole non-empty non-keyed VT base, e.g. RK's
              // `actual_sales · vt` summary): index by the SORTED VT list, not encounter
              // order — same VT set ⇒ same slot on every chart/pin, and no repaint when
              // seriesKeysWindowFirst reorders under a baseline. Honest limit (ROADMAP):
              // differing VT SETS can still drift. Mixed-shape note (hygiene #7g): a null dim
              // VALUE under group-by could theoretically emit an empty-base key beside a named
              // non-keyed base — walk MVs/generator never produce that shape; if it ever
              // appears, both sides index sorted-VT and may share hues per-VT.
              // Keyed neighbors' catalog slots and these sorted-VT slots draw from the SAME
              // palette — a keyed base beside the sole non-keyed base can coincide on a hue
              // (spec-level trade-off, wave-5 D3; RK, the known production instance, has no
              // keyed neighbors).
              (palette[vts.indexOf(rawVt) % palette.length] ?? '#000000')
            : (palette[i % palette.length] ?? '#000000')
    return {
      key,
      label:
        niceVt === null
          ? labelForBase(rawBase)
          : rawBase === ''
            ? niceVt
            : `${labelForBase(rawBase)}${SERIES_VT_SEP}${niceVt}`,
      color: { hex, opacity: 1 },
    }
  })
}

export function fillGaps(points: SeriesPoint[], groups: Group[]): SeriesPoint[] {
  return points.map((p) => {
    const filled: SeriesPoint = { time: p.time }
    for (const g of groups) {
      filled[g.key] = p[g.key]
    }
    return filled
  })
}

export const UNIT_TO_BUCKET_FN: Record<GranularityUnit, (d: Date) => Date> = {
  day: (d) => d,
  week: (d) => startOfWeek(d, { weekStartsOn: 1 }),
  month: (d) => startOfMonth(d),
  quarter: (d) => startOfQuarter(d),
  year: (d) => startOfYear(d),
  // Display-only bucket: rows arrive monthly (toWireBucket maps fy -> 'month') and fold exactly,
  // because a fiscal-year boundary is always a month start.
  fy: (d) => fiscalYearStart(d),
}

export function summariseByUnit(
  points: SeriesPoint[],
  unit: GranularityUnit,
  groups: Group[],
): SeriesPoint[] {
  if (points.length === 0) return []
  const bucketFn = UNIT_TO_BUCKET_FN[unit]
  const byBucket = new Map<string, SeriesPoint>()
  for (const p of points) {
    const bucketKey = format(bucketFn(parseDateLocal(p.time)), 'yyyy-MM-dd')
    const existing = byBucket.get(bucketKey)
    if (!existing) {
      const fresh: SeriesPoint = { time: bucketKey }
      for (const g of groups) {
        fresh[g.key] = p[g.key] === undefined ? undefined : Number(p[g.key])
      }
      byBucket.set(bucketKey, fresh)
    } else {
      for (const g of groups) {
        const v = p[g.key]
        if (v === undefined) continue
        const cur = existing[g.key]
        existing[g.key] = (cur === undefined ? 0 : Number(cur)) + Number(v)
      }
    }
  }
  return [...byBucket.values()].sort((a, b) => String(a.time).localeCompare(String(b.time)))
}

/** The grain coarsened points ACTUALLY carry. Rows arrive bucketed at `rowBucketUnit` (the walk's
 *  governed `bucket_size`) and are then folded at the display `unit`, so the grain they carry is
 *  whichever of the two is COARSER.
 *
 *  Until FY there was nothing to decide: `requested <= effective === bucket_size` always held
 *  (governance only ever coarsens), so the row grain was always the coarser one and every caller
 *  could pass it verbatim. FY is the first DISPLAY unit coarser than its own wire bucket — it
 *  travels as `bucket_size: 'month'` and folds client-side — which inverted that rule. Anything
 *  that classifies a point by its bucket start (the baseline split, the palette's window-first
 *  ordering) must ask THIS, never the wire bucket alone. */
export function pointBucketUnit(
  unit: GranularityUnit,
  rowBucketUnit: GranularityUnit,
): GranularityUnit {
  return unit === 'fy' ? 'fy' : rowBucketUnit
}

/** Row 15-light: the window-from's OWN bucket start (ISO) — the baseline/in-window boundary.
 *  Shared by splitBaseline (coarsened points) and the tiles' raw-row in-window filters so
 *  panel membership and export/total membership can never drift apart. */
export function baselineBoundaryIso(windowFromIso: string, unit: GranularityUnit): string {
  return format(UNIT_TO_BUCKET_FN[unit](parseDateLocal(windowFromIso)), 'yyyy-MM-dd')
}

/** Row 15-light: split extended points at the window-from's OWN bucket boundary — the
 *  boundary bucket stays IN-WINDOW (same membership as the no-baseline rendering). */
export function splitBaseline(
  points: SeriesPoint[],
  windowFromIso: string,
  unit: GranularityUnit,
): { baseline: SeriesPoint[]; inWindow: SeriesPoint[] } {
  const boundary = baselineBoundaryIso(windowFromIso, unit)
  return {
    baseline: points.filter((p) => String(p.time) < boundary),
    inWindow: points.filter((p) => String(p.time) >= boundary),
  }
}

export function partition(points: SeriesPoint[], dotsPerPanel: number): SeriesPoint[][] {
  if (points.length === 0) return []
  const panels: SeriesPoint[][] = []
  for (let i = 0; i < points.length; i += dotsPerPanel) {
    panels.push(points.slice(i, i + dotsPerPanel))
  }
  return panels
}

export interface PartitionWithRemainderResult {
  panels: SeriesPoint[][]
  /** Trailing points NOT rendered as a panel (degenerate stub, < half a panel). */
  hidden: SeriesPoint[]
}

/** Row 18: like partition(), but a trailing chunk shorter than HALF a panel becomes `hidden`
 *  instead of a degenerate stub panel (lone dot, Σ/Δ noise) — provided at least one other
 *  panel exists. A chunk ≥ half panel stays a visible shorter panel (v2 custom-range
 *  behavior). Callers MUST surface non-empty `hidden` visibly (no-silent-fallbacks). */
export function partitionWithRemainder(
  points: SeriesPoint[],
  dotsPerPanel: number,
): PartitionWithRemainderResult {
  const panels = partition(points, dotsPerPanel)
  const last = panels[panels.length - 1]
  if (panels.length >= 2 && last !== undefined && last.length < dotsPerPanel / 2) {
    return { panels: panels.slice(0, -1), hidden: last }
  }
  return { panels, hidden: [] }
}

export interface ApplyCapResult {
  displayed: SeriesPoint[][]
  isTruncated: boolean
  naturalCount: number
}

export function applyCap(panels: SeriesPoint[][], cap: number): ApplyCapResult {
  return {
    displayed: panels.slice(0, cap),
    isTruncated: panels.length > cap,
    naturalCount: panels.length,
  }
}

export interface PanelAggregates {
  sum: number
  avg: number
}

export function computePanelAggregates(panel: SeriesPoint[], groups: Group[]): PanelAggregates {
  if (panel.length === 0) return { sum: 0, avg: 0 }
  let sum = 0
  let nonEmptyRows = 0
  for (const row of panel) {
    let rowHasData = false
    for (const g of groups) {
      const v = row[g.key]
      if (v !== undefined) {
        sum += Number(v)
        rowHasData = true
      }
    }
    if (rowHasData) nonEmptyRows += 1
  }
  const avg = nonEmptyRows === 0 ? 0 : Math.round((sum / nonEmptyRows) * 100) / 100
  return { sum: Math.round(sum * 100) / 100, avg }
}

export function computeGrowth(prev: number, curr: number): number {
  if (prev === 0) return 0
  return Math.round((curr / prev - 1) * 100 * 100) / 100
}

/** Per-series Σ over a panel (volumes): undefined-skipping sum per series key. */
export function computeSeriesSums(panel: SeriesPoint[], groups: Group[]): Map<string, number> {
  const out = new Map<string, number>()
  for (const g of groups) {
    let s = 0
    for (const row of panel) {
      const v = row[g.key]
      if (v !== undefined) s += Number(v)
    }
    out.set(g.key, Math.round(s * 100) / 100)
  }
  return out
}

/** #11 "Running total" for VALUE series (volumes): running cumulative sum per series over the
 *  (already coarsened) value-points. A leading gap (no value yet) stays undefined; a later gap
 *  carries the running total forward (flat) — a cumulative line never drops. Per series independent. */
export function applyRunningTotal(points: SeriesPoint[], groups: Group[]): SeriesPoint[] {
  const running: Record<string, number | undefined> = {}
  return points.map((p) => {
    const out: SeriesPoint = { time: p.time }
    for (const g of groups) {
      const v = p[g.key]
      if (v === undefined) {
        out[g.key] = running[g.key]
      } else {
        running[g.key] = (running[g.key] ?? 0) + Number(v)
        out[g.key] = running[g.key]
      }
    }
    return out
  })
}

/** Nice number (1/2/5 × 10^k) closest to x for axis steps (Heckbert round mode). */
function niceNum(x: number): number {
  const exp = Math.floor(Math.log10(x))
  const f = x / Math.pow(10, exp)
  const nf = f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10
  return nf * Math.pow(10, exp)
}

/** Row 6: loose-axis ceiling — smallest multiple of a nice step (≈ value/4) at or above
 *  value. An EXACT data max as the domain end makes recharts pin the top tick arbitrarily
 *  close to its neighbor → overlapping/garbled top label. 0 stays 0. */
export function niceCeil(value: number): number {
  if (value <= 0) return 0
  const step = niceNum(value / 4)
  const n = Math.ceil(value / step - 1e-9)
  const r = n * step
  return r < value ? r + step : r
}

export function computeDomain(
  panels: SeriesPoint[][],
  groups: Group[],
  isCumulative: boolean,
): [number, number] {
  // Domain is biased to include zero (v2 convention; chart axes always include
  // the zero baseline, so all-positive data has min=0 and all-negative has max=0).
  let min = 0
  let max = 0
  for (const panel of panels) {
    for (const row of panel) {
      if (isCumulative) {
        let stacked = 0
        for (const g of groups) {
          const v = row[g.key]
          if (v !== undefined) stacked += Number(v)
        }
        if (stacked < min) min = stacked
        if (stacked > max) max = stacked
      } else {
        for (const g of groups) {
          const v = row[g.key]
          if (v === undefined) continue
          const n = Number(v)
          if (n < min) min = n
          if (n > max) max = n
        }
      }
    }
  }
  return [min === 0 ? 0 : -niceCeil(-min), niceCeil(max)]
}
